/**
 * VRG-545 — the signups grid's website columns, GENERATED from `WEBSITE_FIELDS`.
 *
 * ## Why generated, and why that is the whole point of the manifest
 *
 * Fifteen hand-written column definitions is exactly what `websiteFields.ts` was created
 * to replace. The failure mode on this surface is silence, not a crash: a field
 * vera-website stops sending renders an em-dash (indistinguishable from a visitor who
 * left it blank), and a field it ADDS lands in no column at all. Three fields — `cac`,
 * `resaleBuyerConversion`, `certificateLeadYield` — survived a review that was looking
 * for exactly them, because a list of columns cannot be compared to anything.
 *
 * A manifest can, and `signups-website-field-coverage.guard.spec.ts` does. Hand-writing
 * the columns would leave that guard asserting something true about a file nothing reads,
 * which is the "unexecuted guard reads as coverage" failure (working-conventions §30) with
 * the guard still passing. So: one column per manifest entry, derived — with ONE derived
 * exception, below, which is declared in the manifest rather than taken here.
 *
 * ## The exception: a field may be MERGED into another field's column
 *
 * `industries` (the ROI form's categories) renders inside `categories` (the waitlist
 * form's), because they are one question asked twice in two vocabularies and two columns
 * showed them as a contradiction. The pairing is declared by `mergedInto` ON THE MANIFEST,
 * not decided here, so the merged field keeps its manifest entry and the drift guard keeps
 * watching it — a rename on vera-website still trips. See `websiteCategories.ts` for why
 * the combination is a union over a normalized key and not a concatenation.
 *
 * ## The rendering rules, each of which is a claim we can or cannot support
 *
 * - **`rate` is a stored RATIO shown as a PERCENT.** `0.023` → `2.3%`. The sort value is
 *   the percent, not the ratio, so a numeric filter matches what the reader can see —
 *   see `websiteSortValue`.
 * - **`currency` takes its symbol FROM THE ROW.** vera-website ships a currency selector
 *   and forwards the chosen ISO 4217 code, so the unit is a recorded fact rather than an
 *   assumption about a stranger's business. Rows predating the selector fall back to USD,
 *   argued at `currencySymbolOf`. ⚠️ Nothing is converted, so two rows' money columns are
 *   not comparable unless their currencies match.
 * - **`number`** gets grouping separators and no unit.
 * - **`list`** is a joined line, except the merged category column, which renders icon
 *   chips — see `CategoryChips`.
 *
 * ## The default marker has TWO strengths, and they render differently — 2026-09-07
 *
 * vera-website forwards `assumptionsTouched`, the ids of the sliders the visitor actually
 * moved (it sits between `assumptions` and `industries` in that repo's `forwardRoi({...})`
 * call). Where that list is present it is the whole answer, and it is the ONLY thing that
 * can distinguish a visitor who deliberately chose 20% from one who never touched the
 * control — the two store the same number.
 *
 * Where it is absent — older submissions, hand-written payloads — the column falls back to
 * comparing the stored value against `websiteDefault` and says so, with different words
 * ("matches default") and a different border (dashed). The fallback is a real signal on the
 * oldest leads, which is why it is not simply discarded; it is also a guess that rots the
 * day the site changes a default (`resaleBuyerConversion` moved 5% → 28% the week this was
 * written), which is why it is never allowed to wear the strong tag.
 *
 * ⚠️ A ROW THAT RECORDS NOTHING AND DIFFERS FROM OUR DEFAULT IS STILL UNMARKED. The absence
 * of a tag means "we do not know", never "the visitor changed it". See `websiteDefaultState`
 * for the four states and `DEFAULT_TAGS` for how each reads.
 */
import React from 'react'

import { Box, Stack, Typography, alpha, useTheme } from '@mui/material'
import { EMPTY_VALUE, Tooltip } from '@repo/ui'

import { CategoryCell, headerWithInfo, INFO_HEADER_ALLOWANCE, MoreCategoriesTooltip } from 'components'
import type { SignupEntry } from 'hooks/api/useSignups'

import { mergedCategories } from './websiteCategories'
import { WEBSITE_FIELDS, type WebsiteField } from './websiteFields'
import {
  currencySymbolOf,
  formatWebsiteDefault,
  formatWebsiteValue,
  websiteDefaultState,
  websiteListItems,
  websiteSortValue,
} from './websiteValues'

const cellSx = { display: 'flex', alignItems: 'center', height: '100%', minWidth: 0 }

/**
 * Per-field notes that are NOT derivable from the manifest.
 *
 * Kept to genuine one-offs. Anything true of a whole `kind` or `group` is composed below
 * instead, so this map cannot quietly become the hand-written column list again.
 */
const FIELD_NOTES: Record<string, string> = {
  /*
    ⚠️ REWRITTEN 2026-09-07 (VRG-545). This note used to say the site "may not forward"
    CAC, so a blank might mean "not sent". It does forward it — verified in vera-website's
    `forwardRoi({...})` call, see the manifest's own corrected caveat — and the old wording
    was actively harmful on a qualification screen: it invited a reviewer to discount a
    figure the visitor really did give us.

    The blank IS meaningful, just differently: the site defaults the field to 0 so it renders
    empty behind a placeholder rather than pre-filling a guess at somebody's CAC, and the
    route collapses that 0 to null on the way out. So a dash here means "asked, not
    answered", which is a real signal and worth saying.
  */
  cac: 'The brand’s own blended cost to acquire one customer. The site leaves this field empty rather than guessing a figure, so a dash means they were asked and did not answer.',
  total: 'The calculator’s own headline figure, kept so we can see what it showed them.',
  categories: 'Both forms ask what the brand sells, and this column shows the answers from either — the waitlist form’s and the ROI calculator’s — combined, with anything picked on both shown once.',
}

/**
 * The header tooltip. Composed per field rather than typed per column, so a new manifest
 * entry gets a correct explanation with no further edit.
 *
 * Returns `null` for a field with nothing to disclose — a waitlist answer is just what the
 * visitor picked, and an info glyph offering "this is a list of categories" is noise that
 * teaches readers to ignore the glyph that mattered.
 */
const headerNote = (field: WebsiteField): string | null => {
  const parts: string[] = []

  if (field.group === 'roi' && field.kind !== 'list') {
    parts.push('Self-reported by the visitor on the marketing site — an estimate typed during a sales evaluation, not a figure Vera measured.')
  }

  const seeded = formatWebsiteDefault(field)
  if (seeded) {
    /*
      Both tags are described, because both are on screen and they mean different things.
      An earlier version said older submissions "carry no tag either way" — true when the
      comparison fallback did not exist, and false the moment it did. A header tooltip that
      tells a reviewer a mark cannot appear, beside rows where it does, is worse than no
      tooltip: it teaches them to read the mark as something else.
    */
    parts.push(
      `The site starts this at ${seeded}. A control the visitor never moved is tagged “default”. Older submissions do not record what was moved, so a figure that merely equals the starting value is tagged “matches default” instead — that one is an inference, not a fact about the visitor.`,
    )
  }

  if (field.kind === 'currency') {
    parts.push(
      'Shown in the currency the visitor picked on the marketing site. Nothing is converted, so figures in different currencies are not comparable. Submissions from before the site asked for a currency are shown as US dollars, which for those rows is an assumption rather than something they told us.',
    )
  }

  const note = FIELD_NOTES[field.id]
  if (note) parts.push(note)

  return parts.length ? parts.join(' ') : null
}

/**
 * The two ways a value can be the site's rather than the visitor's — and they DO NOT render
 * the same, because they are not the same claim.
 *
 *   `default`          the submission RECORDS that this control was never moved.
 *   `default-inferred` the submission records nothing; the value merely EQUALS our default.
 *
 * The wording carries the difference, and so does the border: a solid tag for the recorded
 * fact, a dashed one for the inference. A reviewer who never reads a tooltip still sees two
 * different marks, which is the only version of this that survives contact with a person
 * scanning a queue.
 *
 * ⚠️ The weaker case's copy must NOT say "the visitor did not change it". It cannot know
 * that. It says what is actually true — the figure matches our starting value — and names
 * the reason we cannot go further. Overstating it here would be the same defect as the
 * comparison-as-primary-signal this whole mechanism exists to avoid, just relocated into a
 * string. See `websiteDefaultState`.
 */
const DEFAULT_TAGS = {
  default: {
    label: 'default',
    dashed: false,
    tooltip: "The website's own starting value. This submission records which controls the visitor moved, and this one was not among them.",
  },
  'default-inferred': {
    label: 'matches default',
    dashed: true,
    tooltip:
      "This figure is identical to the website's starting value. The submission does not record which controls were moved — it predates that — so we cannot tell whether the visitor chose it or simply left it alone.",
  },
} as const

/**
 * A value that is, or may be, the site's own seeded figure rather than an answer.
 *
 * Quiet by construction: the figure keeps its place and drops to the secondary tone, with a
 * small neutral tag beside it. It is a provenance footnote, not a warning — a coloured
 * treatment would make the site's own defaults the loudest thing in a row of the visitor's
 * actual answers, which inverts the signal the founder is reading for.
 */
const SeededDefaultValue = ({
  text,
  state,
}: {
  text: string
  state: 'default' | 'default-inferred'
}) => {
  const theme = useTheme()
  const tag = DEFAULT_TAGS[state]
  return (
    <Tooltip title={tag.tooltip}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ ...cellSx }}>
        <Typography variant="body2" color="text.secondary" noWrap>
          {text}
        </Typography>
        <Box
          component="span"
          sx={{
            flexShrink: 0,
            px: 1.5,
            borderRadius: 1.5,
            border: `1px ${tag.dashed ? 'dashed' : 'solid'} ${theme.palette.primary[300]}`,
            backgroundColor: alpha(theme.palette.text.secondary, 0.06),
            color: 'text.secondary',
            fontSize: 10,
            lineHeight: '16px',
            letterSpacing: 0.2,
            whiteSpace: 'nowrap',
          }}
        >
          {tag.label}
        </Box>
      </Stack>
    </Tooltip>
  )
}

/**
 * The merged product-category answer, as the same icon chips the Products table uses.
 *
 * ## Reuse, not a hand-rolled pill
 *
 * `CategoryCell` is the app's single answer to "draw one product category" — it owns the
 * icon map, the border, the label casing — so a chip drawn here matches the one on
 * `/products` by construction rather than by someone matching a border colour by eye
 * (working-conventions §28/§47). It takes a raw string and normalizes it for the icon
 * lookup, which is why `mergedCategories` hands it the platform's DISPLAY LABEL: that is
 * the spelling whose icon key resolves (`Wine & Spirits` → `wine_&_spirits`) AND the one a
 * reviewer should read.
 *
 * ## Overflow is a peek, not a truncation
 *
 * `MoreCategoriesTooltip` is the same "+N more" popover the tag-shop card uses. A brand can
 * pick every category on both forms, and a cell sized for two chips must not silently lose
 * the rest — the header docblock's own argument against chips was that they clip mid-word
 * and lose an answer entirely rather than signalling one exists. An explicit "+N more" is
 * what makes chips survive that argument; without it the old joined-text rendering was the
 * better of the two.
 *
 * ## ⚠️ THE PEEK NEVER SHRINKS; THE CHIPS DO
 *
 * The first version pinned every chip at `flexShrink: 0` and hid the cell's overflow. On a
 * row with two wide categories that pushed the "+N more" clean out of the cell — measured,
 * 386px of content in a 330px box — so the reader saw two chips and NO indication that four
 * more answers existed. That is strictly worse than the joined-text rendering it replaced,
 * which at least ended in an ellipsis.
 *
 * So the peek is `flexShrink: 0` and the chips are not. A long chip label ellipsizes inside
 * its own border, which §49 explicitly permits for a cell VALUE (only a HEADER may never be
 * truncated) and which stays legible because the icon survives; losing the count does not.
 *
 * ## ⚠️ `filter` — THE BORDERLESS FORM — IS WHAT EVERY OTHER CALLER USES
 *
 * This column previously passed `filter={false}`, the BORDERED pill, and then carried a
 * scoped `sx` override that reached three levels into `CategoryCell`'s internals
 * (`> Stack > Stack`, then the label) clearing `min-width` and forcing `alignItems:
 * stretch`, because the bordered chip refused to shrink and drew 68px past its wrapper and
 * over the chip beside it. That override was written against a component that was not
 * misbehaving; it was being asked for the wrong variant.
 *
 * Every OTHER surface that draws a product category passes `filter`:
 *
 *   ProductsPage.tsx:487            `<CategoryCell category={params.value} filter />`
 *   ProductTypeCell.tsx:263         `<CategoryCell category={type} filter />`
 *   TagsShopProductPreview.tsx:310  `<CategoryCell category={category} filter />`
 *
 * `filter` drops the 1px border and collapses the padding from `px: 3 / pr: 4` (12/16px) to
 * `px: 1 / pr: 1` (4/4) — about 22px per chip. It is also simply what a category chip LOOKS
 * like in this app; the bordered form is what `CategoryList` defaults to for standalone
 * lists, not what a table cell uses. So matching Products is both the smaller chip and the
 * consistent one, and neither requires touching the shared component (§28/§47).
 *
 * The other half of what Products does is `display: 'flex'` ON THE COLUMN, which puts the
 * cell itself in flex layout rather than relying on a wrapper Box to do it. That is set on
 * the column definition below.
 *
 * ## Overflow discipline, kept
 *
 * The chips still shrink and the peek still does not (see above), and the row still clips
 * as a backstop. `minWidth: 0` on the chip wrapper is retained — a flex item's default
 * `min-width: auto` refuses to shrink below its content at every level, and that is a fact
 * about flexbox rather than about `CategoryCell`. What is gone is the part that reached
 * INTO the shared component to override its internals.
 */

const CategoryChips = ({ categories }: { categories: string[] }) => {
  // Two, then a peek. Measured: three chips do not fit this column at any realistic label
  // length, and a third chip bought by squeezing the other two is a worse trade than the
  // count.
  const visible = categories.slice(0, 2)
  const hidden = categories.slice(2)

  return (
    <Stack direction="row" spacing={2} alignItems="center" sx={{ ...cellSx, overflow: 'hidden' }}>
      {/*
        ⚠️ The per-chip tooltip is not decoration, it closes a REGRESSION. The rendering
        this replaced was a joined line wrapped in a tooltip carrying the full list, so a
        truncated answer was always recoverable on hover. Chips that ellipsize with no
        tooltip would have made "Auction & r…" unreadable by any means — strictly less
        information than the text it replaced, which is the bar a new rendering has to clear.
      */}
      {visible.map((category) => (
        <Tooltip key={category} title={category}>
          <Box sx={{ display: 'flex', minWidth: 0, flexShrink: 1, overflow: 'hidden' }}>
            <CategoryCell category={category} filter />
          </Box>
        </Tooltip>
      ))}
      {hidden.length > 0 && (
        <MoreCategoriesTooltip categories={hidden} filter>
          <Typography
            variant="body3"
            noWrap
            sx={{
              flexShrink: 0,
              cursor: 'default',
              color: 'text.secondary',
              '&:hover': { color: 'text.primary' },
            }}
          >
            + {hidden.length} more
          </Typography>
        </MoreCategoriesTooltip>
      )}
    </Stack>
  )
}

/**
 * One column per manifest field THAT IS NOT MERGED INTO ANOTHER.
 *
 * A plain function rather than a hook: it closes over nothing, so the caller can hold it in
 * a `useMemo` alongside its own columns without this file needing to know about the render
 * cycle.
 */
export const buildWebsiteColumns = () =>
  WEBSITE_FIELDS.filter((field) => !field.mergedInto).map((field) => {
    /*
      The one field whose value is assembled from BOTH submissions rather than read off one.
      Keyed on being a merge TARGET — `mergedInto` pointing at this id — rather than on the
      literal string 'categories', so moving the merge in the manifest moves the rendering
      with it instead of leaving this branch attached to the wrong column.
    */
    const isCategoryMerge = WEBSITE_FIELDS.some((f) => f.mergedInto === field.id)
    /*
      ONE list resolver, used by the cell, the sort value, the facet options and the filter
      predicate alike. The merged column has two candidate answers to "what is in this
      cell?" — the raw `categories` array and the merged one — and letting any consumer pick
      independently is precisely how a filter comes to disagree with what is on screen.
    */
    const listItemsFor = (row: SignupEntry, f: WebsiteField): string[] =>
      isCategoryMerge ? mergedCategories(row) : websiteListItems(row, f)
    const note = headerNote(field)
    const numeric = field.kind === 'number' || field.kind === 'currency' || field.kind === 'rate'

    return {
      field: field.id,
      headerName: field.label,
      /*
        The second half of what the Products page's Type column does (`ProductsPage.tsx:481`),
        and the reason it is set only on the chip column: `display: 'flex'` puts MUI's own
        cell element into flex layout, so the chips are laid out by the CELL rather than by a
        wrapper Box that has to re-declare `height: 100%` to fake it. Every other column here
        renders one line of text, for which `cellSx` on the child is sufficient and this
        would be noise.
      */
      ...(isCategoryMerge ? { display: 'flex' } : {}),
      /*
        Chooses the FILTER CONTROL, not just the grid's operators. The shared Filters drawer
        renders one field per column keyed on `type`: `number` gets a min/max slider,
        `multiSelect` a checkbox facet list.

        A `list` or `text` answer is a small closed vocabulary — three product categories, a
        plan name — so a facet list is the right control and `string` would be wrong: that
        branch renders `SearchFilter`, whose option machinery resolves against `row[field]`
        and would offer one blank, unlabelled checkbox here (the value lives inside a jsonb
        blob). Ticking it emits the row's id and empties the table — a control that accepts a
        click and can only remove every row. `multiSelect` is an established type in this
        app (9 other columns use it) and MUI treats an unknown type as `string`, so the
        grid's own behaviour is unchanged.
      */
      type: numeric ? 'number' : 'multiSelect',
      ...(field.kind === 'list'
        ? {
            /*
              The facets are the INDIVIDUAL answers, while the cell and the sort value are
              the joined string — see `websiteListItems`. Two hooks, because they answer two
              different questions about the same column.

              ⚠️ The merged column reads through `mergedCategories`, the SAME function the
              cell renders from. Filtering on one list while displaying another is how a
              facet comes to offer `handbags` on a row whose chip reads `Handbags` and match
              nothing — and it would also re-introduce the duplicate the merge removes,
              since the raw lists still hold both spellings.
            */
            filterValueGetter: (_value: unknown, row: SignupEntry) =>
              row ? listItemsFor(row, field) : [],
            /*
              MUI's stock `isAnyOf` compares the CELL value to the selected options, which for
              a joined string means "handbags" never matches "handbags, leather-goods". This
              tests membership instead, resolved from the row rather than by splitting the
              rendered text — a category containing the separator would otherwise split into
              two answers nobody gave.
            */
            filterOperators: [
              {
                value: 'isAnyOf',
                getApplyFilterFn: (filterItem: { value?: unknown }) => {
                  const wanted = Array.isArray(filterItem.value) ? filterItem.value : []
                  // Returning null is MUI's "this filter is not active" — an empty selection
                  // must show everything, not nothing.
                  if (!wanted.length) return null
                  return (_value: unknown, row: SignupEntry) =>
                    listItemsFor(row, field).some((item) => wanted.includes(item))
                },
              },
            ],
          }
        : {}),
      /*
        §49 — a column header is NEVER truncated, and a number column is sized for its
        HEADER, not its values. The manifest's `width` is already computed against the
        label text; the info glyph is a decision made HERE (not a manifest fact), so its
        18px allowance is added here rather than baked into the manifest, where it would
        be wrong for any column that later loses its tooltip.
      */
      width: field.width + (note ? INFO_HEADER_ALLOWANCE : 0),
      // The whole point of the promotion: the panel could be neither of these.
      sortable: true,
      filterable: true,
      ...(note ? { renderHeader: headerWithInfo(field.label, note) } : {}),
      /*
        MUI X v7 signature `(value, row, column, apiRef)` — NOT the v6 `({ value })` shape.
        `row[field.id]` does not exist (the value lives inside a jsonb blob), so `value` is
        always undefined here and the row is the only input.

        This is also what the CSV export and the sort/filter engines read, so it is the one
        place the value is decided. `renderCell` formats whatever this returns and never
        re-derives it — a `valueGetter`/`renderCell` disagreement is how a column comes to
        sort by one number and display another.
      */
      valueGetter: (_value: unknown, row: SignupEntry) => {
        if (!row) return null
        /*
          The merged column's ONE value: the joined merged list. Joined rather than kept as
          an array because this is also the sort key and the CSV export cell, both of which
          need a single comparable string — the chips are rendered from the same list via
          `listItemsFor`, so what is exported, sorted and shown all come from one merge.
        */
        if (isCategoryMerge) {
          const items = listItemsFor(row, field)
          return items.length ? items.join(', ') : null
        }
        return websiteSortValue(row, field)
      },
      renderCell: ({ value, row }: { value: unknown; row: SignupEntry }) => {
        const text = formatWebsiteValue(field, value, currencySymbolOf(row?.roi_submission))
        if (text === null) {
          return (
            <Box sx={cellSx}>
              <Typography variant="body2" color="text.disabled">
                {EMPTY_VALUE}
              </Typography>
            </Box>
          )
        }

        /*
          Chips, not the joined line — and only for the merged category column, because it
          is the only one whose vocabulary the app already draws elsewhere. Placed AFTER the
          empty check so a signup with no categories still gets the dash rather than an
          empty chip row, and before the default-marker check, which cannot apply: that
          marker is scoped to nested assumption fields.
        */
        if (isCategoryMerge) {
          return <CategoryChips categories={listItemsFor(row, field)} />
        }

        const defaultState = websiteDefaultState(row, field)
        if (defaultState === 'default' || defaultState === 'default-inferred') {
          return <SeededDefaultValue text={text} state={defaultState} />
        }

        return (
          <Box sx={cellSx}>
            {/*
              A list is joined into one line and truncated with the column, with the full
              set on hover. Chips were the alternative and do not survive the width: these
              columns are sized for their HEADERS (§49), and three bordered pills in a
              180px cell clip mid-word — which loses the third answer entirely rather than
              signalling that there is one.
            */}
            <Tooltip title={field.kind === 'list' ? text : ''}>
              <Typography variant="body2" noWrap>
                {text}
              </Typography>
            </Tooltip>
          </Box>
        )
      },
    }
  })
